import { Component, useRef, type HTMLAttributes, type ReactNode, type RefObject } from 'react'

import { crossfadeArrival, snapshotRegion, useRefreshLine } from '@/lib/motion/refresh'
import { cn } from '@/lib/utils'

interface ArrivalCrossfadeProps {
  /** The region's own `updating`. */
  updating: boolean
  region: RefObject<HTMLElement | null>
}

/**
 * Crossfades a region's old content into its new one, in the commit where
 * `updating` turns false. A class for the one lifecycle a hook has no
 * equivalent of: `getSnapshotBeforeUpdate` runs after React has worked out
 * the new content and before it writes any of it, the only moment the old
 * content is still on screen to copy. It draws nothing itself.
 */
export class ArrivalCrossfade extends Component<ArrivalCrossfadeProps> {
  override getSnapshotBeforeUpdate(previous: ArrivalCrossfadeProps): HTMLElement | null {
    if (!previous.updating || this.props.updating) return null
    const region = this.props.region.current
    return region === null ? null : snapshotRegion(region)
  }

  override componentDidUpdate(
    _previous: ArrivalCrossfadeProps,
    _state: unknown,
    snapshot: HTMLElement | null,
  ): void {
    const region = this.props.region.current
    if (snapshot !== null && region !== null) crossfadeArrival(region, snapshot)
  }

  override render(): null {
    return null
  }
}

interface RefreshRegionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * The data on show is the PREVIOUS key's, held by `keepPreviousData` while
   * the key the member just picked loads: `isPlaceholderData`, never
   * `isFetching` — a poll or a focus refetch is not showing them the wrong
   * thing, and must not draw a line or replay a crossfade. See UpdatingNote.
   */
  updating: boolean
  /**
   * Cross the old content into the new when it lands. Off for a region whose
   * content has its own transition for that (the heatmap's tiles morph).
   */
  crossfade?: boolean
  children: ReactNode
}

/**
 * The box holding content that a control above it replaces in place — a
 * period, a filter, a page of rows.
 *
 * The old content stays as it was while the new loads: full strength and
 * still live, links and hovers and all, so a member can go on reading it or
 * change their mind; a second change wins, since React Query keeps showing the
 * newest key's data. If the wait outlasts 300ms a thin line sweeps along the
 * region's top edge; when the data lands, the old content fades out over the
 * new as it fades in. Pair it with an `UpdatingNote`, which tells a screen
 * reader what the line shows.
 *
 * It replaced a dim (60% opacity, and a heatmap made inert) with a visible
 * "Updating…", which the owner read as the page locking up for the 0.5–2s a
 * filter takes on a real connection (2026-10-05).
 */
export default function RefreshRegion({
  updating,
  crossfade = true,
  className,
  children,
  ...rest
}: RefreshRegionProps) {
  const ref = useRef<HTMLDivElement>(null)
  useRefreshLine(ref, updating)
  return (
    <div
      ref={ref}
      {...rest}
      aria-busy={updating || undefined}
      className={cn('refresh-region', className)}
    >
      {crossfade ? <ArrivalCrossfade updating={updating} region={ref} /> : null}
      {children}
    </div>
  )
}
